import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getSettings, updateSetting, uploadImage } from "@/api/settings";
import { getServices } from "@/api/services";
import { Building2, Layout, Award, BarChart3, HelpCircle, Save, Loader2, Upload, X as RemoveIcon, Type, Menu as MenuIcon, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export default function AdminSettings() {
  const navigate = useNavigate();
  const [all, setAll] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("company");

  // Form states
  const [company, setCompany] = useState({
    name: "",
    email: "",
    phone: "",
    phone2: "",
    address: "",
    map_url: "",
    map_width: "100%",
    map_height: "450",
    hours_tr: "",
    hours_en: "",
    whatsapp: "",
    slogan_tr: "",
    slogan_en: "",
    twitter: "",
    facebook: "",
    linkedin: "",
    instagram: "",
  });

  const [hero, setHero] = useState({
    title_tr: "",
    title_en: "",
    subtitle_tr: "",
    subtitle_en: "",
    logo_url: "",
  });

  const [about, setAbout] = useState({
    title_tr: "",
    title_en: "",
    body_tr: "",
    body_en: "",
  });

  const [stats, setStats] = useState({
    customers: 0,
    applications: 0,
    countries: 0,
  });

  const [whyUs, setWhyUs] = useState({
    title_tr: "",
    title_en: "",
    body_tr: "",
    body_en: "",
  });

  const [header, setHeader] = useState({
    fontFamily: "",
    fontSize: "15px",
    fontWeight: "500",
    height: "90px",
    logoHeight: "70px",
  });

  const [menu, setMenu] = useState<any[]>([
    { id: "1", title_tr: "Anasayfa", title_en: "Home", path: "/", type: "link" },
    { id: "2", title_tr: "Hakkımızda", title_en: "About Us", path: "/about", type: "link" },
    {
      id: "3",
      title_tr: "Hizmetlerimiz",
      title_en: "Our Services",
      type: "megamenu",
      items: [
        { title_tr: "Tüm Hizmetler", title_en: "All Services", path: "/services", desc_tr: "Tüm hizmetlerimiz", desc_en: "All our services" }
      ]
    },
    { id: "4", title_tr: "Duyurular", title_en: "Announcements", path: "/announcements", type: "link" },
    { id: "5", title_tr: "İletişim", title_en: "Contact", path: "/contact", type: "link" }
  ]);

  const [menuLang, setMenuLang] = useState<"tr" | "en">("tr");
  const [services, setServices] = useState<any[]>([]);

  const [uploading, setUploading] = useState(false);

  const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleUpload(file);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir görsel dosyası seçin.');
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64Data = reader.result as string;
        const res = await uploadImage({ fileName: file.name, base64Data });
        setHero(h => ({ ...h, logo_url: res.url }));
        toast.success('Logo başarıyla yüklendi!');
      } catch (err: any) {
        toast.error('Yükleme başarısız: ' + err.message);
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => {
      toast.error('Dosya okunamadı.');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        const m: any = {};
        data?.forEach((r: any) => {
          try {
            m[r.key] = typeof r.value === 'string' ? JSON.parse(r.value) : r.value;
          } catch (e) {
            m[r.key] = r.value;
          }
        });
        setAll(m);

        // Populate form states
        if (m.company) {
          setCompany({
            name: m.company.name ?? "",
            email: m.company.email ?? "",
            phone: m.company.phone ?? "",
            phone2: m.company.phone2 ?? "",
            address: m.company.address ?? "",
            map_url: m.company.map_url ?? "",
            map_width: m.company.map_width ?? "100%",
            map_height: m.company.map_height ?? "450",
            hours_tr: m.company.hours_tr ?? "",
            hours_en: m.company.hours_en ?? "",
            whatsapp: m.company.whatsapp ?? "",
            slogan_tr: m.company.slogan_tr ?? "",
            slogan_en: m.company.slogan_en ?? "",
            twitter: m.company.social?.twitter ?? "",
            facebook: m.company.social?.facebook ?? "",
            linkedin: m.company.social?.linkedin ?? "",
            instagram: m.company.social?.instagram ?? "",
          });
        }
        if (m.hero) {
          setHero({
            title_tr: m.hero.title_tr ?? "",
            title_en: m.hero.title_en ?? "",
            subtitle_tr: m.hero.subtitle_tr ?? "",
            subtitle_en: m.hero.subtitle_en ?? "",
            logo_url: m.hero.logo_url ?? "",
          });
        }
        if (m.about) {
          setAbout({
            title_tr: m.about.title_tr ?? "",
            title_en: m.about.title_en ?? "",
            body_tr: m.about.body_tr ?? "",
            body_en: m.about.body_en ?? "",
          });
        }
        if (m.stats) {
          setStats({
            customers: Number(m.stats.customers ?? 0),
            applications: Number(m.stats.applications ?? 0),
            countries: Number(m.stats.countries ?? 0),
          });
        }
        if (m.why_us) {
          setWhyUs({
            title_tr: m.why_us.title_tr ?? "",
            title_en: m.why_us.title_en ?? "",
            body_tr: m.why_us.body_tr ?? "",
            body_en: m.why_us.body_en ?? "",
          });
        }
        if (m.header) {
          setHeader({
            fontFamily: m.header.fontFamily ?? "",
            fontSize: m.header.fontSize ?? "15px",
            fontWeight: m.header.fontWeight ?? "500",
            height: m.header.height ?? "90px",
            logoHeight: m.header.logoHeight ?? "70px",
          });
        }
        if (m.menu) {
          setMenu(m.menu);
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to load settings:', err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    getServices(false)
      .then(svcs => {
        console.log("Loaded services in admin settings:", svcs);
        setServices(svcs ?? []);
      })
      .catch(err => {
        console.error("Failed to load services in admin settings:", err);
        toast.error("Hizmet listesi alınırken hata oluştu: " + err.message);
      });
  }, []);

  const handleSave = async (key: string) => {
    setSaving(key);
    let value: any;

    if (key === "company") {
      value = {
        name: company.name,
        email: company.email,
        phone: company.phone,
        phone2: company.phone2,
        address: company.address,
        map_url: company.map_url,
        map_width: company.map_width,
        map_height: company.map_height,
        hours_tr: company.hours_tr,
        hours_en: company.hours_en,
        whatsapp: company.whatsapp,
        slogan_tr: company.slogan_tr,
        slogan_en: company.slogan_en,
        social: {
          twitter: company.twitter,
          facebook: company.facebook,
          linkedin: company.linkedin,
          instagram: company.instagram,
        }
      };
    } else if (key === "hero") {
      value = hero;
    } else if (key === "about") {
      value = about;
    } else if (key === "stats") {
      value = {
        customers: Number(stats.customers),
        applications: Number(stats.applications),
        countries: Number(stats.countries),
      };
    } else if (key === "why_us") {
      value = whyUs;
    } else if (key === "header") {
      value = header;
    } else if (key === "menu") {
      value = menu;
    }

    try {
      await updateSetting({ key, value });
      toast.success("Ayarlar başarıyla kaydedildi!");
      setAll({ ...all, [key]: value });
    } catch (err: any) {
      toast.error(err?.message ?? "Kaydedilemedi");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <span className="text-sm text-muted-foreground font-medium">Ayarlar yükleniyor...</span>
      </div>
    );
  }

  const tabs = [
    { id: "company", label: "Firma Bilgileri", icon: Building2 },
    { id: "hero", label: "Hero Bölümü", icon: Layout },
    { id: "about", label: "Hakkımızda", icon: HelpCircle },
    { id: "stats", label: "İstatistikler", icon: BarChart3 },
    { id: "why_us", label: "Neden Biz", icon: Award },
    { id: "header", label: "Header Yönetimi", icon: Type },
    { id: "menu", label: "Menü Yönetimi", icon: MenuIcon },
  ];

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">Site Ayarları</h1>
        <p className="text-sm text-muted-foreground mt-1">Sitenizin tüm dinamik içeriklerini, firma bilgilerini ve görsellerini buradan yönetebilirsiniz.</p>
      </div>

      {/* TABS SELECTOR */}
      <div className="flex flex-wrap gap-2 mb-6 border-b pb-4">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${activeTab === t.id
              ? "bg-primary text-primary-foreground shadow-sm"
              : "bg-card text-muted-foreground border hover:text-primary hover:bg-accent/40"
              }`}
          >
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ACTIVE TAB CONTENT */}
      <div className="bg-card border rounded-xl shadow-sm p-6">
        {activeTab === "company" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-primary">Firma Genel Bilgileri</h3>
                <p className="text-xs text-muted-foreground mt-0.5">İletişim, adres ve genel slogan ayarları.</p>
              </div>
              <button
                disabled={saving === "company"}
                onClick={() => handleSave("company")}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold cursor-pointer hover:opacity-90 disabled:opacity-50"
              >
                {saving === "company" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Kaydet
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <FormGroup label="Firma Adı">
                <input className="inp" value={company.name} onChange={e => setCompany({ ...company, name: e.target.value })} />
              </FormGroup>
              <FormGroup label="Email Adresi">
                <input type="email" className="inp" value={company.email} onChange={e => setCompany({ ...company, email: e.target.value })} />
              </FormGroup>
              <FormGroup label="Telefon 1">
                <input className="inp" value={company.phone} onChange={e => setCompany({ ...company, phone: e.target.value })} />
              </FormGroup>
              <FormGroup label="Telefon 2">
                <input className="inp" value={company.phone2} onChange={e => setCompany({ ...company, phone2: e.target.value })} />
              </FormGroup>
              <FormGroup label="WhatsApp Numarası (Örn: 905325556789)">
                <input className="inp" value={company.whatsapp} onChange={e => setCompany({ ...company, whatsapp: e.target.value })} />
              </FormGroup>
              <div className="md:col-span-2">
                <FormGroup label="Adres Bilgisi">
                  <textarea className="inp" rows={2} value={company.address} onChange={e => setCompany({ ...company, address: e.target.value })} />
                </FormGroup>
              </div>
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <h4 className="font-bold text-primary mb-3 text-sm">Harita Ayarları</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <FormGroup label="Harita (Google Maps İframe URL)">
                      <input className="inp" placeholder="https://maps.google.com/maps?q=...&output=embed" value={company.map_url} onChange={e => setCompany({ ...company, map_url: e.target.value })} />
                    </FormGroup>
                  </div>
                  <FormGroup label="Harita Genişliği (Örn: 100%, 600px)">
                    <input className="inp" value={company.map_width} onChange={e => setCompany({ ...company, map_width: e.target.value })} placeholder="100%" />
                  </FormGroup>
                  <FormGroup label="Harita Yüksekliği (Örn: 450, 450px)">
                    <input className="inp" value={company.map_height} onChange={e => setCompany({ ...company, map_height: e.target.value })} placeholder="450" />
                  </FormGroup>
                </div>
              </div>
              <FormGroup label="Çalışma Saatleri (TR)">
                <input className="inp" value={company.hours_tr} onChange={e => setCompany({ ...company, hours_tr: e.target.value })} />
              </FormGroup>
              <FormGroup label="Çalışma Saatleri (EN)">
                <input className="inp" value={company.hours_en} onChange={e => setCompany({ ...company, hours_en: e.target.value })} />
              </FormGroup>
              <FormGroup label="Slogan (TR)">
                <input className="inp" value={company.slogan_tr} onChange={e => setCompany({ ...company, slogan_tr: e.target.value })} />
              </FormGroup>
              <FormGroup label="Slogan (EN)">
                <input className="inp" value={company.slogan_en} onChange={e => setCompany({ ...company, slogan_en: e.target.value })} />
              </FormGroup>

              {/* Sosyal Medya */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <h4 className="font-bold text-primary mb-3 text-sm">Sosyal Medya Linkleri</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormGroup label="Instagram Linki">
                    <input className="inp" value={company.instagram} onChange={e => setCompany({ ...company, instagram: e.target.value })} />
                  </FormGroup>
                  <FormGroup label="Facebook Linki">
                    <input className="inp" value={company.facebook} onChange={e => setCompany({ ...company, facebook: e.target.value })} />
                  </FormGroup>
                  <FormGroup label="LinkedIn Linki">
                    <input className="inp" value={company.linkedin} onChange={e => setCompany({ ...company, linkedin: e.target.value })} />
                  </FormGroup>
                  <FormGroup label="Twitter Linki">
                    <input className="inp" value={company.twitter} onChange={e => setCompany({ ...company, twitter: e.target.value })} />
                  </FormGroup>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "hero" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-primary">Hero Bölümü</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Anasayfanın en üstündeki karşılama alanı içerikleri.</p>
              </div>
              <button
                disabled={saving === "hero"}
                onClick={() => handleSave("hero")}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold cursor-pointer hover:opacity-90 disabled:opacity-50"
              >
                {saving === "hero" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Kaydet
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <FormGroup label="Ana Başlık (TR)">
                    <input className="inp" value={hero.title_tr} onChange={e => setHero({ ...hero, title_tr: e.target.value })} />
                  </FormGroup>
                  <FormGroup label="Ana Başlık (EN)">
                    <input className="inp" value={hero.title_en} onChange={e => setHero({ ...hero, title_en: e.target.value })} />
                  </FormGroup>
                </div>
                <FormGroup label="Alt Başlık / Açıklama (TR)">
                  <textarea className="inp" rows={3} value={hero.subtitle_tr} onChange={e => setHero({ ...hero, subtitle_tr: e.target.value })} />
                </FormGroup>
                <FormGroup label="Alt Başlık / Açıklama (EN)">
                  <textarea className="inp" rows={3} value={hero.subtitle_en} onChange={e => setHero({ ...hero, subtitle_en: e.target.value })} />
                </FormGroup>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider">Hero Logosu (Şeffaf / Sadece Logo)</label>
                {hero.logo_url ? (
                  <div className="relative border rounded-xl p-4 bg-accent/10 flex flex-col items-center justify-center min-h-[260px] border-solid">
                    <img src={hero.logo_url.startsWith('http')
                      ? hero.logo_url
                      : `${API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL}${hero.logo_url.startsWith('/') ? '' : '/'}${hero.logo_url}`} alt="Hero Logo" className="max-h-[160px] object-contain mb-3" />
                    <button
                      type="button"
                      onClick={() => setHero({ ...hero, logo_url: "" })}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-destructive text-destructive-foreground hover:opacity-90 cursor-pointer shadow-sm z-20"
                      title="Logoyu Kaldır"
                    >
                      <RemoveIcon size={14} />
                    </button>
                    <span className="text-xs text-muted-foreground font-medium">Logo başarıyla yüklendi</span>
                  </div>
                ) : (
                  <div
                    onDragOver={e => e.preventDefault()}
                    onDrop={handleFileDrop}
                    className="border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-center min-h-[260px] bg-card hover:bg-accent/10 hover:border-primary transition-all group relative cursor-pointer"
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="absolute inset-0 opacity-0 cursor-pointer z-10"
                    />
                    {uploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        <span className="text-sm font-semibold text-muted-foreground">Logo yükleniyor...</span>
                      </div>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                          <Upload size={20} />
                        </div>
                        <p className="text-sm font-bold text-primary">Logoyu buraya sürükleyin veya tıklayın</p>
                        <p className="text-xs text-muted-foreground mt-1">PNG, SVG veya JPG (Şeffaf arka plan önerilir)</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "about" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-primary">Hakkımızda Sayfası</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Kurumsal hakkımızda metinleri ve başlıkları.</p>
              </div>
              <button
                disabled={saving === "about"}
                onClick={() => handleSave("about")}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold cursor-pointer hover:opacity-90 disabled:opacity-50"
              >
                {saving === "about" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Kaydet
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <FormGroup label="Hakkımızda Başlığı (TR)">
                  <input className="inp" value={about.title_tr} onChange={e => setAbout({ ...about, title_tr: e.target.value })} />
                </FormGroup>
                <FormGroup label="Hakkımızda Başlığı (EN)">
                  <input className="inp" value={about.title_en} onChange={e => setAbout({ ...about, title_en: e.target.value })} />
                </FormGroup>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <FormGroup label="Hakkımızda Metni (TR)">
                  <textarea className="inp" rows={12} value={about.body_tr} onChange={e => setAbout({ ...about, body_tr: e.target.value })} />
                </FormGroup>
                <FormGroup label="Hakkımızda Metni (EN)">
                  <textarea className="inp" rows={12} value={about.body_en} onChange={e => setAbout({ ...about, body_en: e.target.value })} />
                </FormGroup>
              </div>
            </div>
          </div>
        )}

        {activeTab === "stats" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-primary">Sayaç İstatistikleri</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Anasayfada yer alan animasyonlu sayaç rakamları.</p>
              </div>
              <button
                disabled={saving === "stats"}
                onClick={() => handleSave("stats")}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold cursor-pointer hover:opacity-90 disabled:opacity-50"
              >
                {saving === "stats" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Kaydet
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <FormGroup label="Mutlu Müşteriler (Customers)">
                <input type="number" className="inp" value={stats.customers} onChange={e => setStats({ ...stats, customers: Number(e.target.value) })} />
              </FormGroup>
              <FormGroup label="Tamamlanan Başvurular (Applications)">
                <input type="number" className="inp" value={stats.applications} onChange={e => setStats({ ...stats, applications: Number(e.target.value) })} />
              </FormGroup>
              <FormGroup label="İş Birliği Yapılan Ülkeler (Countries)">
                <input type="number" className="inp" value={stats.countries} onChange={e => setStats({ ...stats, countries: Number(e.target.value) })} />
              </FormGroup>
            </div>
          </div>
        )}

        {activeTab === "why_us" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-primary">Neden Biz?</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Anasayfadaki sayaçların üstünde bulunan kurumsal açıklama alanı.</p>
              </div>
              <button
                disabled={saving === "why_us"}
                onClick={() => handleSave("why_us")}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold cursor-pointer hover:opacity-90 disabled:opacity-50"
              >
                {saving === "why_us" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Kaydet
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <FormGroup label="Neden Biz Başlığı (TR)">
                  <input className="inp" value={whyUs.title_tr} onChange={e => setWhyUs({ ...whyUs, title_tr: e.target.value })} />
                </FormGroup>
                <FormGroup label="Neden Biz Başlığı (EN)">
                  <input className="inp" value={whyUs.title_en} onChange={e => setWhyUs({ ...whyUs, title_en: e.target.value })} />
                </FormGroup>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <FormGroup label="Açıklama Metni (TR)">
                  <textarea className="inp" rows={5} value={whyUs.body_tr} onChange={e => setWhyUs({ ...whyUs, body_tr: e.target.value })} />
                </FormGroup>
                <FormGroup label="Açıklama Metni (EN)">
                  <textarea className="inp" rows={5} value={whyUs.body_en} onChange={e => setWhyUs({ ...whyUs, body_en: e.target.value })} />
                </FormGroup>
              </div>
            </div>
          </div>
        )}
        {activeTab === "header" && (
          <div className="space-y-6 animate-fade-in-up">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-primary">Header (Menü) Yönetimi</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Sitenin üst menü (header) alanının tasarımını, yazı tiplerini ve boyutlarını buradan özelleştirebilirsiniz.</p>
              </div>
              <button
                disabled={saving === "header"}
                onClick={() => handleSave("header")}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold cursor-pointer hover:opacity-90 disabled:opacity-50"
              >
                {saving === "header" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Kaydet
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4 bg-accent/5 p-5 rounded-2xl border border-solid">
                <h4 className="text-sm font-bold text-primary border-b pb-2 mb-3">Yazı Tipi & Boyut Ayarları</h4>

                <FormGroup label="Menü Yazı Tipi (Font Family)">
                  <select
                    className="inp cursor-pointer"
                    value={header.fontFamily}
                    onChange={e => setHeader({ ...header, fontFamily: e.target.value })}
                  >
                    <option value="">Sistem Varsayılanı (System Font)</option>
                    <option value="'Inter', sans-serif">Inter</option>
                    <option value="'Outfit', sans-serif">Outfit</option>
                    <option value="'Poppins', sans-serif">Poppins</option>
                    <option value="'Montserrat', sans-serif">Montserrat</option>
                    <option value="'Roboto', sans-serif">Roboto</option>
                    <option value="'Nunito', sans-serif">Nunito</option>
                    <option value="'Fira Sans', sans-serif">Fira Sans</option>
                    <option value="'Ubuntu', sans-serif">Ubuntu</option>
                    <option value="'Lora', serif">Lora (Serif)</option>
                    <option value="'Merriweather', serif">Merriweather (Serif)</option>
                    <option value="'Playfair Display', serif">Playfair Display (Serif)</option>
                  </select>
                </FormGroup>

                <div className="grid grid-cols-2 gap-4">
                  <FormGroup label="Yazı Boyutu (Font Size)">
                    <select
                      className="inp cursor-pointer"
                      value={header.fontSize}
                      onChange={e => setHeader({ ...header, fontSize: e.target.value })}
                    >
                      <option value="12px">12px</option>
                      <option value="13px">13px</option>
                      <option value="14px">14px</option>
                      <option value="15px">15px (Önerilen)</option>
                      <option value="16px">16px</option>
                      <option value="17px">17px</option>
                      <option value="18px">18px</option>
                      <option value="19px">19px</option>
                      <option value="20px">20px</option>
                    </select>
                  </FormGroup>

                  <FormGroup label="Yazı Kalınlığı (Font Weight)">
                    <select
                      className="inp cursor-pointer"
                      value={header.fontWeight}
                      onChange={e => setHeader({ ...header, fontWeight: e.target.value })}
                    >
                      <option value="400">Regular (400)</option>
                      <option value="500">Medium (500)</option>
                      <option value="600">Semibold (600)</option>
                      <option value="700">Bold (700)</option>
                    </select>
                  </FormGroup>
                </div>
              </div>

              <div className="space-y-4 bg-accent/5 p-5 rounded-2xl border border-solid">
                <h4 className="text-sm font-bold text-primary border-b pb-2 mb-3 font-semibold">Yerleşim & Boyutlandırma</h4>

                <div className="grid grid-cols-2 gap-4">
                  <FormGroup label="Header Yüksekliği (Header Height)">
                    <select
                      className="inp cursor-pointer"
                      value={header.height}
                      onChange={e => setHeader({ ...header, height: e.target.value })}
                    >
                      <option value="70px">70px</option>
                      <option value="80px">80px</option>
                      <option value="90px">90px (Mevcut)</option>
                      <option value="100px">100px</option>
                      <option value="110px">110px</option>
                      <option value="120px">120px</option>
                    </select>
                  </FormGroup>

                  <FormGroup label="Logo Yüksekliği (Logo Height)">
                    <select
                      className="inp cursor-pointer"
                      value={header.logoHeight}
                      onChange={e => setHeader({ ...header, logoHeight: e.target.value })}
                    >
                      <option value="30px">30px</option>
                      <option value="40px">40px</option>
                      <option value="50px">50px</option>
                      <option value="60px">60px</option>
                      <option value="70px">70px (Mevcut)</option>
                      <option value="80px">80px</option>
                    </select>
                  </FormGroup>
                </div>

                <div className="mt-4 p-3 bg-primary/5 rounded-xl border border-primary/20 text-xs text-primary font-medium flex flex-col gap-1 leading-relaxed">
                  <span className="font-bold">Önizleme İpucu:</span>
                  <span>Seçtiğiniz font ve boyutlar hem masaüstü hem de mobil menülerde anında aktif hale gelecektir. Google Fonts kütüphanesinden seçilen yazı tipleri optimize edilmiş olarak yüklenir.</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "menu" && (
          <div className="space-y-6 animate-fade-in-up">
            <div className="flex flex-wrap justify-between items-center gap-4 border-b pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-primary">Menü (Navigasyon) Yönetimi</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Sitenizin üst kısmında yer alan menü linklerini, açılır menüleri (dropdown) ve mega menüleri yönetin.</p>
              </div>
              <div className="flex items-center gap-4">
                {/* Dil Seçici (Language Selector) */}
                <div className="flex bg-accent/20 p-0.5 rounded-lg border border-solid">
                  <button
                    onClick={() => setMenuLang("tr")}
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-all cursor-pointer ${menuLang === "tr"
                      ? "bg-primary text-white shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                      }`}
                  >
                    Türkçe (TR)
                  </button>
                  <button
                    onClick={() => setMenuLang("en")}
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-all cursor-pointer ${menuLang === "en"
                      ? "bg-primary text-white shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                      }`}
                  >
                    English (EN)
                  </button>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const newItem = {
                        id: Date.now().toString(),
                        title_tr: "Yeni Menü",
                        title_en: "New Menu",
                        path: "/yeni",
                        type: "link",
                        items: []
                      };
                      setMenu([...menu, newItem]);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 text-xs font-semibold cursor-pointer"
                  >
                    <Plus size={14} /> Yeni Menü Ekle
                  </button>
                  <button
                    disabled={saving === "menu"}
                    onClick={() => handleSave("menu")}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold cursor-pointer hover:opacity-90 disabled:opacity-50"
                  >
                    {saving === "menu" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Kaydet
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {menu.map((item, idx) => (
                <div key={item.id} className="bg-accent/5 p-4 rounded-2xl border border-solid space-y-4 relative transition-all duration-200 hover:shadow-sm">
                  {/* Main Item Header */}
                  <div className="flex flex-wrap items-center justify-between gap-4 bg-accent/10 -mx-4 -mt-4 p-3 rounded-t-2xl border-b">
                    <div className="flex items-center gap-3">
                      <span className="bg-primary/10 text-primary text-xs font-bold px-2 py-0.5 rounded-md">#{idx + 1}</span>
                      <h4 className="text-sm font-bold text-primary">{item.title_tr || "Yeni Menü"}</h4>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        disabled={idx === 0}
                        onClick={() => {
                          const newMenu = [...menu];
                          const temp = newMenu[idx];
                          newMenu[idx] = newMenu[idx - 1];
                          newMenu[idx - 1] = temp;
                          setMenu(newMenu);
                        }}
                        className="p-1.5 rounded bg-background border hover:bg-accent/10 disabled:opacity-30 cursor-pointer"
                        title="Yukarı Taşı"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        disabled={idx === menu.length - 1}
                        onClick={() => {
                          const newMenu = [...menu];
                          const temp = newMenu[idx];
                          newMenu[idx] = newMenu[idx + 1];
                          newMenu[idx + 1] = temp;
                          setMenu(newMenu);
                        }}
                        className="p-1.5 rounded bg-background border hover:bg-accent/10 disabled:opacity-30 cursor-pointer"
                        title="Aşağı Taşı"
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm("Bu menüyü ve altındaki tüm elemanları silmek istediğinize emin misiniz?")) {
                            setMenu(menu.filter(m => m.id !== item.id));
                          }
                        }}
                        className="p-1.5 rounded bg-destructive/10 text-destructive hover:bg-destructive/20 border-transparent cursor-pointer"
                        title="Menüyü Sil"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Main Item Inputs */}
                  <div className="grid md:grid-cols-3 gap-4">
                    {menuLang === "tr" ? (
                      <FormGroup label="Menü Adı (TR)">
                        <input
                          className="inp"
                          value={item.title_tr}
                          onChange={e => {
                            const newMenu = [...menu];
                            newMenu[idx].title_tr = e.target.value;
                            setMenu(newMenu);
                          }}
                        />
                      </FormGroup>
                    ) : (
                      <FormGroup label="Menü Adı (EN)">
                        <input
                          className="inp"
                          value={item.title_en}
                          onChange={e => {
                            const newMenu = [...menu];
                            newMenu[idx].title_en = e.target.value;
                            setMenu(newMenu);
                          }}
                        />
                      </FormGroup>
                    )}
                    <FormGroup label="Menü Tipi">
                      <select
                        className="inp cursor-pointer"
                        value={item.type}
                        onChange={e => {
                          const newMenu = [...menu];
                          newMenu[idx].type = e.target.value;
                          if (e.target.value === "megamenu" && !newMenu[idx].items) {
                            newMenu[idx].items = [];
                          }
                          setMenu(newMenu);
                        }}
                      >
                        <option value="link">Doğrudan Link</option>
                        <option value="megamenu">Açılır Menü / Dropdown (Mega Menü)</option>
                      </select>
                    </FormGroup>
                    {item.type === "link" && (
                      <FormGroup label="Gideceği Adres (URL/Path)">
                        <input
                          className="inp"
                          value={item.path || ""}
                          placeholder="Örn: /about veya dış link"
                          onChange={e => {
                            const newMenu = [...menu];
                            newMenu[idx].path = e.target.value;
                            setMenu(newMenu);
                          }}
                        />
                      </FormGroup>
                    )}
                  </div>

                  {/* Dropdown Items Section */}
                  {item.type === "megamenu" && (
                    <div className="border-t pt-4 mt-2 space-y-3 pl-4 md:pl-8 border-dashed border-primary/20">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-primary flex items-center gap-1.5">
                          <MenuIcon size={12} /> Alt Elemanlar (Sub-items)
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              if (services.length === 0) {
                                toast.error("İçe aktarılacak hizmet bulunamadı.");
                                return;
                              }
                              const newMenu = [...menu];
                              if (!newMenu[idx].items) newMenu[idx].items = [];
                              const currentItems = newMenu[idx].items;
                              // Map all services into menu item format
                              const importedItems = services.map(s => ({
                                title_tr: s.title_tr,
                                title_en: s.title_en,
                                path: `/services/${s.slug}`
                              }));
                              newMenu[idx].items = [...currentItems, ...importedItems];
                              setMenu(newMenu);
                              toast.success(`${services.length} hizmet başarıyla eklendi!`);
                            }}
                            className="flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-solid border-emerald-200 text-[11px] font-bold cursor-pointer transition-colors shadow-2xs"
                          >
                            Tüm Hizmetleri Ekle
                          </button>
                          <button
                            onClick={() => {
                              const newMenu = [...menu];
                              if (!newMenu[idx].items) newMenu[idx].items = [];
                              newMenu[idx].items.push({
                                title_tr: "Yeni Alt Eleman",
                                title_en: "New Sub-item",
                                path: ""
                              });
                              setMenu(newMenu);
                            }}
                            className="flex items-center gap-1 px-2.5 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 text-[11px] font-semibold cursor-pointer"
                          >
                            <Plus size={12} /> Alt Eleman Ekle
                          </button>
                        </div>
                      </div>

                      {(!item.items || item.items.length === 0) ? (
                        <div className="text-xs text-muted-foreground bg-accent/5 p-3 rounded-lg border border-dashed text-center">
                          Henüz bir alt eleman eklenmemiş. "Alt Eleman Ekle" butonuna basarak ekleyebilirsiniz.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {item.items.map((sub: any, sIdx: number) => (
                            <div key={sIdx} className="bg-background p-3 rounded-xl border border-solid flex flex-col gap-3 relative shadow-2xs">
                              {/* Sub Item Top Buttons */}
                              <div className="flex justify-between items-center border-b pb-1.5">
                                <span className="text-[11px] font-bold text-muted-foreground">Alt Eleman #{sIdx + 1}</span>
                                <div className="flex items-center gap-1">
                                  <button
                                    disabled={sIdx === 0}
                                    onClick={() => {
                                      const newMenu = [...menu];
                                      const temp = newMenu[idx].items[sIdx];
                                      newMenu[idx].items[sIdx] = newMenu[idx].items[sIdx - 1];
                                      newMenu[idx].items[sIdx - 1] = temp;
                                      setMenu(newMenu);
                                    }}
                                    className="p-1 rounded hover:bg-accent/10 disabled:opacity-30 cursor-pointer"
                                    title="Yukarı Taşı"
                                  >
                                    <ArrowUp size={12} />
                                  </button>
                                  <button
                                    disabled={sIdx === item.items.length - 1}
                                    onClick={() => {
                                      const newMenu = [...menu];
                                      const temp = newMenu[idx].items[sIdx];
                                      newMenu[idx].items[sIdx] = newMenu[idx].items[sIdx + 1];
                                      newMenu[idx].items[sIdx + 1] = temp;
                                      setMenu(newMenu);
                                    }}
                                    className="p-1 rounded hover:bg-accent/10 disabled:opacity-30 cursor-pointer"
                                    title="Aşağı Taşı"
                                  >
                                    <ArrowDown size={12} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const newMenu = [...menu];
                                      newMenu[idx].items = newMenu[idx].items.filter((_: any, i: number) => i !== sIdx);
                                      setMenu(newMenu);
                                    }}
                                    className="p-1 rounded text-destructive hover:bg-destructive/10 cursor-pointer"
                                    title="Alt Elemanı Sil"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>

                              {/* Bir Hizmet Bağla (Opsiyonel) */}
                              <div className="w-full">
                                <FormGroup label="Bir Hizmet Bağla (Seçildiğinde alanları otomatik doldurur)">
                                  <select
                                    className="inp text-xs cursor-pointer"
                                    value={
                                      services.find(
                                        s => sub.path === `/services/${s.slug}`
                                      )?.id || ""
                                    }
                                    onChange={e => {
                                      const selectedId = e.target.value;
                                      const newMenu = [...menu];
                                      if (selectedId) {
                                        const svc = services.find(s => s.id === selectedId);
                                        if (svc) {
                                          newMenu[idx].items[sIdx].title_tr = svc.title_tr;
                                          newMenu[idx].items[sIdx].title_en = svc.title_en;
                                          newMenu[idx].items[sIdx].path = `/services/${svc.slug}`;
                                          newMenu[idx].items[sIdx].desc_tr = svc.short_tr || "";
                                          newMenu[idx].items[sIdx].desc_en = svc.short_en || "";
                                        }
                                      } else {
                                        newMenu[idx].items[sIdx].path = "";
                                      }
                                      setMenu(newMenu);
                                    }}
                                  >
                                    <option value="">-- Özel Link (Kendiniz yazın) --</option>
                                    {services.map(s => (
                                      <option key={s.id} value={s.id}>
                                        {s.title_tr} ({s.title_en})
                                      </option>
                                    ))}
                                  </select>
                                </FormGroup>
                              </div>

                              {/* Sub Item Inputs */}
                              <div className="grid md:grid-cols-2 gap-3">
                                {menuLang === "tr" ? (
                                  <FormGroup label="Alt Eleman Adı (TR)">
                                    <input
                                      className="inp text-xs"
                                      value={sub.title_tr}
                                      onChange={e => {
                                        const newMenu = [...menu];
                                        newMenu[idx].items[sIdx].title_tr = e.target.value;
                                        setMenu(newMenu);
                                      }}
                                    />
                                  </FormGroup>
                                ) : (
                                  <FormGroup label="Alt Eleman Adı (EN)">
                                    <input
                                      className="inp text-xs"
                                      value={sub.title_en}
                                      onChange={e => {
                                        const newMenu = [...menu];
                                        newMenu[idx].items[sIdx].title_en = e.target.value;
                                        setMenu(newMenu);
                                      }}
                                    />
                                  </FormGroup>
                                )}
                                <FormGroup label="Gideceği Adres (URL/Path)">
                                  <input
                                    className="inp text-xs"
                                    value={sub.path}
                                    onChange={e => {
                                      const newMenu = [...menu];
                                      newMenu[idx].items[sIdx].path = e.target.value;
                                      setMenu(newMenu);
                                    }}
                                  />
                                </FormGroup>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .inp {
          width: 100%;
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 10px 12px;
          background: var(--background);
          font-size: 0.875rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .inp:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.15);
        }
      `}</style>
    </div>
  );
}

function FormGroup({ label, children }: any) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
